<template>
  <v-card flat class="pa-4">
    <div class="ua-head">
      <span class="uh-title">Utilizatori aplicație</span>
      <div class="uh-actions">
        <DxButton text="Import Excel" icon="upload" styling-mode="outlined" @click="openImport" />
        <DxButton text="Creează utilizator" icon="add" type="default" styling-mode="contained" @click="openCreate" />
      </div>
    </div>
    <v-divider class="my-3"></v-divider>

    <DxDataGrid
      :data-source="users"
      key-expr="ID"
      :show-borders="true"
      :show-column-lines="false"
      :show-row-lines="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
    >
      <DxSearchPanel :visible="true" placeholder="Caută utilizator..." />
      <DxPaging :page-size="10" />
      <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 20, 50]" show-info />

      <DxColumn data-field="Username" caption="Utilizator" />
      <DxColumn data-field="Email" caption="Email" />
      <DxColumn data-field="Active" caption="Activ" :width="120" alignment="center" cell-template="activeCell" />
      <DxColumn data-field="SuperAdmin" caption="SuperAdmin" :width="120" alignment="center" cell-template="boolCell" />
      <DxColumn data-field="ADAccount" caption="Cont AD" :width="110" alignment="center" cell-template="boolCell" />
      <DxColumn caption="" :width="230" cell-template="actions" />

      <template #activeCell="{ data }">
        <span class="ua-badge" :class="data.data.Active ? 'on' : 'off'">
          {{ data.data.Active ? 'Activ' : 'Inactiv' }}
        </span>
      </template>

      <template #boolCell="{ data }">
        <v-icon v-if="data.value" color="#059669" size="18">mdi-check-circle</v-icon>
        <v-icon v-else color="#cbd5e1" size="18">mdi-minus-circle-outline</v-icon>
      </template>

      <template #actions="{ data }">
        <div class="row-actions">
          <DxButton
            :text="data.data.Active ? 'Dezactivează' : 'Activează'"
            styling-mode="text"
            @click="toggleActive(data.data)"
          />
          <DxButton icon="key" hint="Resetează parola" styling-mode="text" @click="openReset(data.data)" />
          <DxButton icon="event" hint="Companii" styling-mode="text" @click="openCompanies(data.data)" />
        </div>
      </template>
    </DxDataGrid>

    <!-- Popup confirmare reset parola -->
    <DxPopup v-model:visible="resetVisible" title="Resetare parolă" :width="420" height="auto"
      :drag-enabled="false" :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="del-body">
        <p>Sigur vrei să resetezi parola pentru <strong>„{{ toReset && toReset.Username }}"</strong>?</p>
        <p class="del-warn">Se va genera o parolă nouă și se va trimite pe email.</p>
        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Resetează" type="default" styling-mode="contained" @click="confirmReset" />
          <DxButton text="Anulează" styling-mode="outlined" @click="resetVisible = false" />
        </div>
      </div>
    </DxPopup>

    <!-- Popup creare utilizator -->
    <DxPopup v-model:visible="createVisible" title="Creează utilizator" :width="480" height="auto"
      :drag-enabled="false" :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="firma-form">
        <div class="ff-row">
          <div class="ff-field ff-full">
            <label>Utilizator</label>
            <DxTextBox v-model:value="createForm.Username" placeholder="ion.popescu" />
          </div>
        </div>
        <div class="ff-row">
          <div class="ff-field ff-full">
            <label>Email</label>
            <DxTextBox v-model:value="createForm.Email" placeholder="ion.popescu@exemplu.ro" />
          </div>
        </div>
        <div class="ff-row">
          <div class="ff-field ff-full">
            <label>Parolă (opțional — se generează automat dacă lipsește)</label>
            <DxTextBox v-model:value="createForm.Password" mode="password" placeholder="•••••••••" />
          </div>
        </div>
        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Creează" type="default" styling-mode="contained" @click="submitCreate" />
          <DxButton text="Anulează" styling-mode="outlined" @click="createVisible = false" />
        </div>
      </div>
    </DxPopup>

    <!-- Popup import Excel -->
    <DxPopup v-model:visible="importVisible" title="Import utilizatori din Excel" :width="520" height="auto"
      :drag-enabled="false" :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="firma-form">
        <p class="import-hint">
          Coloane: <strong>Username, Email, Parola, Firme</strong> (firmele se separă prin virgulă).
          Parola poate fi lăsată goală — se generează automat.
        </p>
        <div class="ff-row">
          <a href="#" class="import-template-link" @click.prevent="downloadTemplate">
            <i class="dx-icon dx-icon-download"></i> Descarcă template
          </a>
        </div>
        <div class="ff-row">
          <DxFileUploader
            :multiple="false"
            accept=".csv,.xlsx,.xls"
            upload-mode="useForm"
            select-button-text="Alege fișier"
            label-text="sau trage fișierul aici"
            @value-changed="onImportFileChanged"
          />
        </div>

        <div v-if="importResult" class="import-result">
          <p><strong>{{ importResult.Creati }}</strong> conturi create,
             <strong>{{ importResult.AdaugatiLaFirme }}</strong> alocări la firme.</p>
          <div v-if="importResult.Erori && importResult.Erori.length" class="import-errors">
            <p class="import-errors-title">Erori:</p>
            <ul>
              <li v-for="(e, i) in importResult.Erori" :key="i">{{ e }}</li>
            </ul>
          </div>
        </div>

        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Importă" type="default" styling-mode="contained"
            :disabled="!importFile" @click="submitImport" />
          <DxButton text="Închide" styling-mode="outlined" @click="importVisible = false" />
        </div>
      </div>
    </DxPopup>

    <!-- Popup companii utilizator (gestiune: adaugă / șterge) -->
    <DxPopup v-model:visible="companiesVisible" :title="`Companii — ${companiesUsername}`" :width="520" height="auto"
      :drag-enabled="false" :show-close-button="true" :close-on-outside-click="true"
      :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="companies-body">
        <div v-if="companiesLoading" class="companies-empty">Se încarcă...</div>
        <div v-else-if="companiesList.length === 0" class="companies-empty">Nicio companie asociată.</div>
        <div v-else class="companies-grid">
          <div v-for="c in companiesList" :key="c.ID" class="comp-row">
            <span class="comp-avatar">{{ ini(c.Name) }}</span>
            <span class="comp-name">{{ c.Name }}</span>
            <span v-if="c.Active === false" class="comp-inactive">inactivă</span>
            <DxButton icon="trash" styling-mode="text" type="danger" hint="Scoate din firmă"
              @click="removeCompany(c)" />
          </div>
        </div>

        <div class="companies-add-row">
          <DxSelectBox
            v-model:value="companyToAdd"
            :data-source="companiesNotAdded"
            display-expr="Name"
            value-expr="ID"
            placeholder="Alege o firmă..."
            style="flex: 1"
          />
          <DxButton text="Adaugă" type="default" styling-mode="contained"
            :disabled="!companyToAdd" @click="addCompany" />
        </div>

        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Închide" styling-mode="outlined" @click="companiesVisible = false" />
        </div>
      </div>
    </DxPopup>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import DxDataGrid, { DxColumn, DxPaging, DxPager, DxSearchPanel } from "devextreme-vue/data-grid";
import { DxPopup } from "devextreme-vue/popup";
import { DxButton } from "devextreme-vue/button";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxFileUploader } from "devextreme-vue/file-uploader";
import notify from "devextreme/ui/notify";
import UserAdminService from "@/services/AUTH/UserAdmin";
import CompanyService from "@/services/CompanyService";

export default defineComponent({
  name: "UserAdminGrid",
  components: {
    DxDataGrid, DxColumn, DxPaging, DxPager, DxSearchPanel, DxPopup, DxButton,
    DxTextBox, DxSelectBox, DxFileUploader,
  },
  setup() {
    const users = ref<any[]>([]);
    const resetVisible = ref(false);
    const toReset = ref<any>(null);

    const companiesVisible = ref(false);
    const companiesUsername = ref("");
    const companiesUserCompanyId = ref("");
    const companiesList = ref<any[]>([]);
    const companiesLoading = ref(false);
    const allCompanies = ref<any[]>([]);
    const companyToAdd = ref<string | null>(null);

    const createVisible = ref(false);
    const createForm = ref<{ Username: string; Email: string; Password: string }>({
      Username: "", Email: "", Password: "",
    });

    const importVisible = ref(false);
    const importFile = ref<File | null>(null);
    const importResult = ref<any>(null);

    const load = () => {
      UserAdminService.getAll().then(res => { users.value = res.data; }).catch(console.error);
    };

    const ini = (name: string) => {
      const p = (name || '').trim().split(/\s+/).filter(Boolean);
      return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : (p[0]?.[1] || ''))).toUpperCase();
    };

    const toggleActive = async (row: any) => {
      try {
        await UserAdminService.setActive(row.ID, !row.Active);
        notify(!row.Active ? 'Utilizator activat.' : 'Utilizator dezactivat.', 'success', 2000);
        load();
      } catch (e: any) {
        notify(e?.response?.data?.Message || 'Eroare la actualizarea stării.', 'error', 4000);
      }
    };

    const openReset = (row: any) => { toReset.value = row; resetVisible.value = true; };

    const confirmReset = async () => {
      const row = toReset.value; if (!row) return;
      try {
        await UserAdminService.resetPassword(row.ID);
        notify('Parolă resetată, email trimis.', 'success', 3000);
        resetVisible.value = false;
      } catch (e: any) {
        notify(e?.response?.data?.message || 'Eroare la resetarea parolei.', 'error', 4000);
      }
    };

    // ---------------- Creare utilizator ----------------
    const openCreate = () => {
      createForm.value = { Username: "", Email: "", Password: "" };
      createVisible.value = true;
    };

    const submitCreate = async () => {
      if (!createForm.value.Username || !createForm.value.Email) {
        notify('Utilizator și Email sunt obligatorii.', 'warning', 3000);
        return;
      }
      try {
        const res = await UserAdminService.createUser({
          Username: createForm.value.Username,
          Email: createForm.value.Email,
          Password: createForm.value.Password || undefined,
        });
        const pwd = res?.data?.Password;
        notify(pwd ? `Utilizator creat. Parolă: ${pwd}` : 'Utilizator creat.', 'success', 6000);
        createVisible.value = false;
        load();
      } catch (e: any) {
        notify(e?.response?.data || e?.response?.data?.Message || 'Eroare la crearea utilizatorului.', 'error', 4000);
      }
    };

    // ---------------- Import Excel ----------------
    const openImport = () => {
      importFile.value = null;
      importResult.value = null;
      importVisible.value = true;
    };

    const onImportFileChanged = (e: any) => {
      importFile.value = e.value?.[0] ?? null;
    };

    const downloadTemplate = async () => {
      try {
        const res = await UserAdminService.downloadImportTemplate();
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "template_import_utilizatori.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e: any) {
        notify('Eroare la descărcarea template-ului.', 'error', 4000);
      }
    };

    const submitImport = async () => {
      if (!importFile.value) return;
      try {
        const res = await UserAdminService.importUsers(importFile.value);
        importResult.value = res.data;
        notify('Import finalizat.', 'success', 3000);
        load();
      } catch (e: any) {
        notify(e?.response?.data || 'Eroare la import.', 'error', 4000);
      }
    };

    // ---------------- Gestiune companii ----------------
    const companiesNotAdded = computed(() => {
      const addedIds = new Set(companiesList.value.map((c: any) => c.ID));
      return allCompanies.value.filter((c: any) => !addedIds.has(c.ID));
    });

    const openCompanies = async (row: any) => {
      companiesUsername.value = row.Username;
      companiesUserCompanyId.value = row.ID;
      companiesList.value = [];
      companyToAdd.value = null;
      companiesLoading.value = true;
      companiesVisible.value = true;
      try {
        const [companiesRes, allRes] = await Promise.all([
          UserAdminService.getCompanies(row.Username),
          allCompanies.value.length ? Promise.resolve({ data: allCompanies.value }) : CompanyService.getAll(),
        ]);
        companiesList.value = companiesRes.data;
        allCompanies.value = allRes.data;
      } catch (e: any) {
        notify(e?.response?.data?.Message || 'Eroare la încărcarea companiilor.', 'error', 4000);
      } finally {
        companiesLoading.value = false;
      }
    };

    const reloadCompaniesList = async () => {
      const res = await UserAdminService.getCompanies(companiesUsername.value);
      companiesList.value = res.data;
    };

    const addCompany = async () => {
      if (!companyToAdd.value) return;
      try {
        await UserAdminService.addToCompany(companiesUserCompanyId.value, companyToAdd.value);
        notify('Firmă adăugată.', 'success', 2000);
        companyToAdd.value = null;
        await reloadCompaniesList();
      } catch (e: any) {
        notify(e?.response?.data?.Message || e?.response?.data || 'Eroare la adăugarea firmei.', 'error', 4000);
      }
    };

    const removeCompany = async (company: any) => {
      try {
        await UserAdminService.removeFromCompany(companiesUserCompanyId.value, company.ID);
        notify('Firmă eliminată.', 'success', 2000);
        await reloadCompaniesList();
      } catch (e: any) {
        notify(e?.response?.data?.Message || e?.response?.data || 'Eroare la eliminarea firmei.', 'error', 4000);
      }
    };

    onMounted(load);
    return {
      users, resetVisible, toReset, openReset, confirmReset,
      companiesVisible, companiesUsername, companiesList, companiesLoading, openCompanies,
      companiesNotAdded, companyToAdd, addCompany, removeCompany,
      createVisible, createForm, openCreate, submitCreate,
      importVisible, importFile, importResult, openImport, onImportFileChanged, downloadTemplate, submitImport,
      toggleActive, ini,
    };
  }
});
</script>

<style scoped>
.ua-head { display: flex; align-items: center; justify-content: space-between; }
.uh-title { font-size: 1.1rem; font-weight: 600; color: #1e293b; }
.uh-actions { display: flex; gap: 8px; }
.row-actions { display: flex; gap: 2px; }

.ua-badge { font-size: 11px; font-weight: 700; padding: 2px 12px; border-radius: 20px; }
.ua-badge.on { background: #ecfdf5; color: #059669; }
.ua-badge.off { background: #f1f5f9; color: #94a3b8; }

.ff-row { margin-bottom: 12px; }
.ff-field { display: flex; flex-direction: column; gap: 4px; }
.ff-field label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.5px; }
.ff-full { width: 100%; }
.ff-actions { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.ff-spacer { flex: 1; }

.del-body { display: flex; flex-direction: column; gap: 8px; padding: 6px 2px; font-size: 14px; color: #334155; }
.del-warn { color: #94a3b8; font-size: 12.5px; }

.firma-form { padding: 4px 2px; }
.import-hint { font-size: 12.5px; color: #94a3b8; margin-bottom: 10px; }
.import-template-link { display: inline-flex; align-items: center; gap: 6px; color: #ff2c21; font-size: 13.5px; font-weight: 600; text-decoration: none; }
.import-template-link:hover { text-decoration: underline; }
.import-result { margin-top: 12px; padding: 10px 12px; background: #f8f9fc; border: 1px solid #f1f5f9; border-radius: 10px; font-size: 13.5px; color: #334155; }
.import-errors { margin-top: 6px; }
.import-errors-title { font-weight: 700; color: #b91c1c; font-size: 12px; margin-bottom: 4px; }
.import-errors ul { margin: 0; padding-left: 18px; color: #b91c1c; font-size: 12.5px; }

.companies-body { padding: 4px 2px; font-size: 14px; color: #334155; }
.companies-empty { color: #94a3b8; font-size: 13px; padding: 14px 0; text-align: center; }
.companies-grid { display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow-y: auto; margin-bottom: 12px; }
.comp-row {
  display: flex; align-items: center; gap: 11px;
  padding: 8px 10px; background: #f8f9fc; border: 1px solid #f1f5f9; border-radius: 10px;
}
.comp-avatar {
  flex-shrink: 0; width: 32px; height: 32px; border-radius: 9px;
  background: rgba(255, 44, 33, 0.1); color: #ff2c21;
  font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center;
}
.comp-name { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 500; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.comp-inactive { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #94a3b8; background: #f1f5f9; padding: 2px 8px; border-radius: 20px; }
.companies-add-row { display: flex; align-items: center; gap: 8px; padding-top: 8px; border-top: 1px solid #f1f5f9; }
</style>
